import Tone from 'tone';
import StepBucket from 'StepBucket';

const TIME_16N = new Tone.Time('16n');
// resolution represented in the pattern
const baseResolution = 32;
const baseTripletResolution = 32;

// Represents Resolution of a beat, for example 1/32 notes or 1/32t (triplet notes)
class Resolution {
    constructor(resolution=16, isTriplet=false) {
        this.resolution = resolution;
        this.isTriplet = isTriplet;
        this.baseResolution = isTriplet ? 48 : 32;
    }

    // if this is a triplet resolution, then returns the non triplet version
    getTripletOrNonTriplet() {
        if (this.isTriplet) {
            return new Resolution(this.resolution, false);
        } else {
            return new Resolution(this.resolution, true);
        }
    }

    equals(o) {
        return this.resolution === o.resolution &&
            this.isTriplet === o.isTriplet;
    }

    getOffset() {
        return this.baseResolution / this.numSteps();
    }

    getLengthAtResolution(length) {
        let ratio = this.numSteps() / this.baseResolution;
        return length * ratio;
    }
    
    // returns number of 1/32 steps for 1 step at this
    // resolution
    getNumberOfStepsAtBaseResolution() {
        let x = this.baseResolution / this.numSteps();
        if (this.isTriplet) {
            return x * (16.0/24.0);
        }
        return x;
    }

    getTimeInStep() {
        return this.isTriplet ? new Tone.Time(this.resolution + 't') :
            new Tone.Time(this.resolution + 'n');
    }

    getDurationOfStep() {
        return this.getTimeInStep().toMilliseconds() /
            new TIME_16N.toMilliseconds();
    }

    numSteps() {
        if (this.isTriplet) {
            return (this.resolution / 32) * 48;
        } else {
            return this.resolution;
        }
    }

    toString() {
        return this.isTriplet ? this.resolution + 't' : this.resolution;
    }

    // returns a list (given at the baseResolution) to
    // the given resolution, i.e. it will have length
    // numSteps()
    transformList(list, resolution) {
        resolution = resolution || this.baseResolution;
        let offset = resolution / this.numSteps();
        let transformedList = [];
        for (let i=0; i < list.length; i += offset) {
            transformedList.push(list[i]);
        }
        return transformedList;
    }

    // given a list of buckets of length (in the base resolution of 1/32)
    // returns a list of StepData in this resolution.
    // The list of steps displayed in the UI comes from the function
    // For example, to see 1/16t you need to get back 12 steps instead of
    // the 32. 
    transformBucketsIntoSteps(buckets) {
        let offset = this.baseResolution / this.numSteps();
        let steps = [];
        for (var i=0; i < buckets.length; i += offset) {
            var minKey = 16;
            var step = null;
            for (var j=0; j < offset; j++) {
                let bucket = buckets[i + j];
                if (bucket === undefined) {
                    break;
                }
                var stepsInBucket = bucket.steps;
                for (var key in stepsInBucket) {
                    if (key < minKey && stepsInBucket[key].on) {
                        minKey = key;
                        step = j;
                    }
                }
            }
            if (step == null) {
                step = 0;
                minKey = 0;
            }

            steps.push(buckets[i + step].getStep(minKey));
        }
        return steps;
    }

    // given a list of buckets at a resolution, transform the
    // list into THIS resolution
    transformBucketsAtResolution(buckets, resolution, start, end, output) {
        if (this.isTriplet == resolution.isTriplet) {
            return this.transformList(buckets, resolution);
        }

        // otherwise one is triplet and the other is not, start
        // with the given list of buckets at non-triplet
        let len = buckets.length * this.numSteps() / resolution.numSteps();
        let track = buckets[0].track;

        let t = output || [];
        if (output === undefined) {
            for (let i=0; i < len; i++) {
                let bucket = new StepBucket(track, this.isTriplet);
                t.push(bucket);
            }
        }

        if (end === undefined) {
            end = buckets.length;
        }
        if (start === undefined) {
            start = 0;
        }
        // want to transform into a triplet list
        for (let i=start; i < end ; i++) {
            for (var key in buckets[i].steps) {
                let triplet = (i + parseFloat(key)) * this.numSteps()/resolution.numSteps();
                let tripletIndex = Math.floor(triplet);
                let bucketKey = triplet - tripletIndex;
                let tripletBucket = t[tripletIndex];
                if (tripletBucket !== undefined) {
                    tripletBucket.setStep(bucketKey, buckets[i].getStep(key));
                    t[tripletIndex] = tripletBucket;
                }
            }
        }

        return t;
    }

    // transforms the step at the given resolution to the resolution
    // of this class's resolution instance
    transformStepAtResolution(step, resolution) {
        let factor = this.baseResolution / this.numSteps();
        return step * factor * this.numSteps() / resolution.numSteps();
    }

    transformStepAtResolution2(step, resolution) {
        return step * this.numSteps() / resolution.numSteps();
    }

    transformStepAtBaseResolution(step) {
        return step * new Resolution(baseResolution, false).numSteps() / this.numSteps();
    }

    transformStepFromBaseResolution(step) {
        return this.transformStepAtResolution2(step, baseResolutionObject);
    }

    getJson() {
        return {
            resolution: this.resolution,
            baseResolution: this.baseResolution,
            isTriplet: this.isTriplet,
        };
    }

    fromJson(json) {
        this.resolution = json.resolution;
        this.baseResolution = json.baseResolution;
        this.isTriplet = json.isTriplet;
        return this;
    }

    getCopy() {
        return new Resolution(this.resolution, this.isTriplet);
    }
}

const baseResolutionObject = new Resolution(baseResolution, false);

export default Resolution;
